// frontend/src/modules/admin/components/ParametresFerme.jsx
import React, { useState, useEffect } from 'react';
import { Save, Upload } from 'lucide-react';
import ModeTestToggle from './ModeTestToggle';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const ParametresFerme = ({ config: configFromProps, onConfigUpdate }) => {
  const [config, setConfig] = useState(configFromProps || {
    nom_ferme: '',
    adresse: '',
    telephone: '',
    email_contact: '',
    siret: '',
    lien_decouverte: '',
    logo_url: '',
    smtp_host: '',
    smtp_port: '',
    smtp_user: '',
    smtp_pass: '',
    email_expediteur_nom: '',
    email_reponse: '',
    reseau_social_1_type: '',
    reseau_social_1_url: '',
    reseau_social_2_type: '',
    reseau_social_2_url: '',
    lien_cgv: '',
    lien_politique_rgpd: '',
    lien_conditions_retractation: ''
  });

  useEffect(() => {
    if (configFromProps) {
      setConfig(prev => ({ ...prev, ...configFromProps }));
    }
  }, [configFromProps])

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Liste des réseaux sociaux disponibles avec leurs icônes
  const reseauxDisponibles = [
    { id: 'facebook', nom: 'Facebook', icon: '📘', color: 'bg-blue-600' },
    { id: 'instagram', nom: 'Instagram', icon: '📷', color: 'bg-pink-600' },
    { id: 'twitter', nom: 'Twitter/X', icon: '🐦', color: 'bg-sky-500' },
    { id: 'youtube', nom: 'YouTube', icon: '📺', color: 'bg-red-600' },
    { id: 'linkedin', nom: 'LinkedIn', icon: '🔗', color: 'bg-blue-700' },
    { id: 'tiktok', nom: 'TikTok', icon: '🎵', color: 'bg-black' },
    { id: 'pinterest', nom: 'Pinterest', icon: '📌', color: 'bg-red-500' },
    { id: 'snapchat', nom: 'Snapchat', icon: '👻', color: 'bg-yellow-400' },
    { id: 'whatsapp', nom: 'WhatsApp', icon: '💬', color: 'bg-green-500' },
    { id: 'telegram', nom: 'Telegram', icon: '✈️', color: 'bg-blue-400' }
  ];

  useEffect(() => {
    loadConfig();
  }, []);

const loadConfig = async () => {
    try {
      const response = await fetch(`${API_BASE}/configuration`);
      const data = await response.json();
      
      const configObj = {};
      data.forEach(item => {
        configObj[item.param_name] = item.param_value || '';
      });
      
      setConfig(prev => ({ ...prev, ...configObj }));
      setLoading(false);
      
      // ⚡ Notifier AdminDashboard si callback fourni
      if (onConfigUpdate) {
        onConfigUpdate();
      }
    } catch (error) {
      console.error('Erreur chargement config:', error);
      setLoading(false);
    }
  };

  


  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleReseauSelect = (numero, reseauId) => {
    const field = `reseau_social_${numero}_type`;
    
    // Si on clique sur le même réseau, on le désélectionne
    if (config[field] === reseauId) {
      handleChange(field, '');
      handleChange(`reseau_social_${numero}_url`, '');
    } else {
      handleChange(field, reseauId);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    
    try {
      const promises = Object.entries(config).map(([key, value]) => {
        return fetch(`${API_BASE}/configuration/${key}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ param_value: value })
        });
      });
      
      await Promise.all(promises);
      
      setMessage('✅ Configuration enregistrée avec succès !');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      setMessage('❌ Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">Chargement de la configuration...</div>
      </div>
    );
  }

  const getReseauInfo = (type) => {
    return reseauxDisponibles.find(r => r.id === type);
  };

  return (

    <div className="space-y-6">
       {/* ⚡ MODE TEST - NOUVEAU */}
      <ModeTestToggle 
        config={config} 
        onConfigUpdate={loadConfig}
      />
      {/* En-tête */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 rounded-lg p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">⚙️ Paramètres de la Ferme</h2>
        <p className="text-green-100">Configuration générale de votre exploitation</p>
      </div>

      {/* Message de confirmation */}
      {message && (
        <div className={`p-4 rounded-lg ${message.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message}
        </div>
      )}

      {/* Informations générales */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Informations Générales</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nom de la ferme</label>
            <input
              type="text"
              value={config.nom_ferme}
              onChange={(e) => handleChange('nom_ferme', e.target.value)}
              className="w-full p-2 border rounded-md"
              placeholder="La Ferme du Petit Essart"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SIRET</label>
            <input
              type="text"
              value={config.siret}
              onChange={(e) => handleChange('siret', e.target.value)}
              className="w-full p-2 border rounded-md"
              placeholder="123 456 789 00012"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Adresse complète</label>
            <textarea
              value={config.adresse}
              onChange={(e) => handleChange('adresse', e.target.value)}
              className="w-full p-2 border rounded-md"
              rows="2"
              placeholder="123 rue de Bois Petit Couronne, 76650 Petit-Couronne"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
            <input
              type="tel"
              value={config.telephone}
              onChange={(e) => handleChange('telephone', e.target.value)}
              className="w-full p-2 border rounded-md"
              placeholder="02 35 66 977"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email de contact</label>
            <input
              type="email"
              value={config.email_contact}
              onChange={(e) => handleChange('email_contact', e.target.value)}
              className="w-full p-2 border rounded-md"
              placeholder="contact@ferme.fr"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Lien vidéo promo / Découverte</label>
            <input
              type="url"
              value={config.lien_decouverte}
              onChange={(e) => handleChange('lien_decouverte', e.target.value)}
              className="w-full p-2 border rounded-md"
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </div>
        </div>
      </div>

      {/* Réseaux sociaux */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">🌐 Réseaux Sociaux (max 2)</h3>
        
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
          <p className="text-sm text-blue-800">
            💡 <strong>Mode d'emploi :</strong> Cliquez sur 2 icônes pour les sélectionner, 
            puis saisissez les URLs de vos pages. Les icônes apparaîtront sur la page de connexion.
          </p>
        </div>

        {/* Grille de sélection */}
        <div className="mb-6">
          <h4 className="font-medium text-gray-700 mb-3">Choisissez 2 réseaux sociaux :</h4>
          <div className="grid grid-cols-5 gap-3">
            {reseauxDisponibles.map(reseau => {
              const isSelected1 = config.reseau_social_1_type === reseau.id;
              const isSelected2 = config.reseau_social_2_type === reseau.id;
              const isSelected = isSelected1 || isSelected2;
              const isDisabled = !isSelected && config.reseau_social_1_type && config.reseau_social_2_type;

              return (
                <button
                  key={reseau.id}
                  onClick={() => {
                    if (isSelected1) {
                      handleReseauSelect(1, reseau.id);
                    } else if (isSelected2) {
                      handleReseauSelect(2, reseau.id);
                    } else if (!config.reseau_social_1_type) {
                      handleReseauSelect(1, reseau.id);
                    } else if (!config.reseau_social_2_type) {
                      handleReseauSelect(2, reseau.id);
                    }
                  }}
                  disabled={isDisabled}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isSelected
                      ? `${reseau.color} text-white border-gray-900 scale-105`
                      : isDisabled
                      ? 'bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed'
                      : 'bg-white border-gray-300 hover:border-green-500 hover:scale-105'
                  }`}
                >
                  <div className="text-3xl mb-1">{reseau.icon}</div>
                  <div className="text-xs font-medium">{reseau.nom}</div>
                  {isSelected && (
                    <div className="text-xs mt-1">
                      {isSelected1 ? '1️⃣' : '2️⃣'}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* URLs des réseaux sélectionnés */}
        <div className="space-y-4">
          {/* Réseau 1 */}
          {config.reseau_social_1_type && (
            <div className="bg-gray-50 p-4 rounded-lg border">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">{getReseauInfo(config.reseau_social_1_type)?.icon}</span>
                <h4 className="font-medium text-gray-900">
                  1️⃣ {getReseauInfo(config.reseau_social_1_type)?.nom}
                </h4>
              </div>
              <input
                type="url"
                value={config.reseau_social_1_url}
                onChange={(e) => handleChange('reseau_social_1_url', e.target.value)}
                className="w-full p-2 border rounded-md"
                placeholder={`URL de votre page ${getReseauInfo(config.reseau_social_1_type)?.nom}`}
              />
            </div>
          )}

          {/* Réseau 2 */}
          {config.reseau_social_2_type && (
            <div className="bg-gray-50 p-4 rounded-lg border">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">{getReseauInfo(config.reseau_social_2_type)?.icon}</span>
                <h4 className="font-medium text-gray-900">
                  2️⃣ {getReseauInfo(config.reseau_social_2_type)?.nom}
                </h4>
              </div>
              <input
                type="url"
                value={config.reseau_social_2_url}
                onChange={(e) => handleChange('reseau_social_2_url', e.target.value)}
                className="w-full p-2 border rounded-md"
                placeholder={`URL de votre page ${getReseauInfo(config.reseau_social_2_type)?.nom}`}
              />
            </div>
          )}
        </div>
      </div>

      {/* Mentions légales - Éditeurs Markdown */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">⚖️ Mentions Légales</h3>
          <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            Format Markdown
          </div>
        </div>

        {/* Guide Markdown rapide */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <details className="cursor-pointer">
            <summary className="font-medium text-blue-900 text-sm">
              📘 Guide Markdown (cliquez pour voir)
            </summary>
            <div className="mt-3 text-xs text-gray-700 space-y-1 font-mono">
              <p><strong># Titre principal</strong></p>
              <p><strong>## Titre section</strong></p>
              <p><strong>### Sous-titre</strong></p>
              <p><strong>**texte gras**</strong> → <strong>texte gras</strong></p>
              <p><strong>*texte italique*</strong> → <em>texte italique</em></p>
              <p><strong>- Item de liste</strong></p>
              <p><strong>[lien](https://url.com)</strong></p>
            </div>
          </details>
        </div>
        
        <div className="space-y-6">
          {/* CGV */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              📜 Conditions Générales de Vente (CGV)
            </label>
            <p className="text-xs text-gray-600 mb-3">
              Ces conditions s'afficheront sur la page d'accueil en popup
            </p>
            <textarea
              value={config.cgv || ''}
              onChange={(e) => handleChange('cgv', e.target.value)}
              className="w-full p-3 border rounded-md font-mono text-sm"
              rows="8"
              placeholder="# Conditions Générales de Vente

## Article 1 - Objet
Ces conditions régissent les réservations...

## Article 2 - Prix
Les prix sont indiqués en euros TTC...

**Important :** Les réservations sont fermes et définitives."
            />
            <div className="mt-2 text-xs text-gray-500">
              {config.cgv ? `${config.cgv.length} caractères` : 'Vide'}
            </div>
          </div>

          {/* RGPD */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              🔒 Politique de Confidentialité (RGPD)
            </label>
            <p className="text-xs text-gray-600 mb-3">
              Informations sur la collecte et l'utilisation des données
            </p>
            <textarea
              value={config.politique_rgpd || ''}
              onChange={(e) => handleChange('politique_rgpd', e.target.value)}
              className="w-full p-3 border rounded-md font-mono text-sm"
              rows="8"
              placeholder="# Politique de Confidentialité

## Collecte des données
Nous collectons les informations suivantes :
- Nom et prénom
- Adresse email
- Numéro de téléphone

## Utilisation des données
Vos données sont utilisées uniquement pour :
- Gérer vos réservations
- Vous contacter en cas de besoin

## Vos droits
Conformément au RGPD, vous disposez d'un droit d'accès, de modification et de suppression de vos données.

Contact : [email_contact]"
            />
            <div className="mt-2 text-xs text-gray-500">
              {config.politique_rgpd ? `${config.politique_rgpd.length} caractères` : 'Vide'}
            </div>
          </div>

          {/* Rétractation */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              ↩️ Conditions de Rétractation
            </label>
            <p className="text-xs text-gray-600 mb-3">
              Politique d'annulation et de remboursement
            </p>
            <textarea
              value={config.conditions_retractation || ''}
              onChange={(e) => handleChange('conditions_retractation', e.target.value)}
              className="w-full p-3 border rounded-md font-mono text-sm"
              rows="8"
              placeholder="# Conditions de Rétractation

## Délai d'annulation
Les annulations sont possibles jusqu'à **48 heures** avant la date de retrait prévue.

## Modalités
Pour annuler une réservation :
1. Contactez-nous par email : [email_contact]
2. Indiquez votre numéro de réservation
3. Le remboursement sera effectué sous 7 jours

## Exceptions
*Les produits frais ne peuvent être annulés moins de 48h avant le retrait.*"
            />
            <div className="mt-2 text-xs text-gray-500">
              {config.conditions_retractation ? `${config.conditions_retractation.length} caractères` : 'Vide'}
            </div>
          </div>
        </div>

        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            💡 <strong>Astuce :</strong> Ces mentions apparaîtront en bas de la page d'accueil. 
            Laissez vide pour ne pas afficher un lien.
          </p>
        </div>
      </div>

      {/* Configuration Email SMTP */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📧 Configuration Email (SMTP)</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Serveur SMTP</label>
            <input
              type="text"
              value={config.smtp_host}
              onChange={(e) => handleChange('smtp_host', e.target.value)}
              className="w-full p-2 border rounded-md"
              placeholder="ssl0.ovh.net"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Port SMTP</label>
            <input
              type="number"
              value={config.smtp_port}
              onChange={(e) => handleChange('smtp_port', e.target.value)}
              className="w-full p-2 border rounded-md"
              placeholder="587"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Utilisateur SMTP</label>
            <input
              type="text"
              value={config.smtp_user}
              onChange={(e) => handleChange('smtp_user', e.target.value)}
              className="w-full p-2 border rounded-md"
              placeholder="noreply@votre-ferme.fr"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe SMTP</label>
            <input
              type="password"
              value={config.smtp_pass}
              onChange={(e) => handleChange('smtp_pass', e.target.value)}
              className="w-full p-2 border rounded-md"
              placeholder="••••••••"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nom expéditeur</label>
            <input
              type="text"
              value={config.email_expediteur_nom}
              onChange={(e) => handleChange('email_expediteur_nom', e.target.value)}
              className="w-full p-2 border rounded-md"
              placeholder="Farm-Booking"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email réponse</label>
            <input
              type="email"
              value={config.email_reponse}
              onChange={(e) => handleChange('email_reponse', e.target.value)}
              className="w-full p-2 border rounded-md"
              placeholder="contact@votre-ferme.fr"
            />
          </div>
        </div>
      </div>

      {/* Bouton Sauvegarder */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white ${
            saving
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          <Save className="h-5 w-5" />
          {saving ? 'Enregistrement...' : 'Enregistrer la Configuration'}
        </button>
      </div>
    </div>
  );
};

export default ParametresFerme;